import type { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson'
import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl'

import type { Layer } from '../layer'
import { nearestIndex } from './timeSource'

/**
 * The sixth `Layer` kind: a run's worth of vector frames on **one** geojson
 * source, swapped in place with `setData`.
 *
 * The relationship to `ImageFramesLayer` is exact, and so is the reason for it:
 *
 * | | `ImageFramesLayer` | this |
 * |---|---|---|
 * | source | one `image` | one `geojson` |
 * | swap | `updateImage` | `setData` |
 * | why one | no viewport culling; 112 MB of texture | 96 frames mounted is 96 render trees |
 * | preload | the HTTP cache, `immutable` | the same |
 * | prefetch | −1/+3 around the cursor | the same |
 *
 * `GeoJsonLayer` is to a refresh timer what this is to a run's frames: that one
 * fetches one document and re-fetches it on a clock, this one holds a list of
 * documents on a forecast axis and never re-fetches any of them, because a
 * (model, run, var, kind, lead) URL is immutable by construction.
 *
 * **The one-source rule is as load-bearing here as it is for the image layer,
 * for a different reason.** Two mounted geojson sources do not cross-fade;
 * they simply both draw, which on an isobar field is two sets of isobars an
 * hour apart superimposed and reads as noise rather than as a mistake.
 *
 * How the frames are *styled* is the caller's: `addLayers` gets the map, the
 * source id and the insertion point and adds whatever line or symbol layers
 * the product needs. This class owns the source, the axis and the swapping,
 * which is all that is common between an isobar and a wind barb.
 */

export interface VectorFrame {
  /** The instant this frame is valid for. */
  time: Date
  url: string
}

export type VectorCollection = FeatureCollection<Geometry, GeoJsonProperties>

export interface GeoJsonFramesOptions {
  id: string
  attribution: string
  opacity: number
  /**
   * Resolve the frames. Must resolve rather than reject: a layer that cannot
   * load its frames degrades to drawing nothing, which is the contract
   * `Layer.load()` states.
   */
  loadFrames(): Promise<{ frames: VectorFrame[]; attribution?: string } | null>
  /**
   * Add the style layers that draw this product, and return their ids top
   * down for removal. Called on every mount, so it must be idempotent-safe in
   * the same way `add()` is — the caller is given a map whose source already
   * exists.
   */
  addLayers(
    map: MapLibreMap,
    sourceId: string,
    opacity: number,
    beforeId?: string,
  ): string[]
  /** Called after each swap, for a layer whose paint depends on the data. */
  onFrame?(map: MapLibreMap, collection: VectorCollection): void
  prefetchAhead?: number
  prefetchBehind?: number
}

const DEFAULT_AHEAD = 3
const DEFAULT_BEHIND = 1

const EMPTY: VectorCollection = { type: 'FeatureCollection', features: [] }

export class GeoJsonFramesLayer implements Layer {
  readonly id: string
  private frames: VectorFrame[] = []
  private map: MapLibreMap | null = null
  private current = 0
  private opacity: number
  private visible = true
  private loaded: Promise<void> | null = null
  private styleLayers: string[] = []
  private attribution: string
  /**
   * The documents already fetched, keyed by URL.
   *
   * Unlike the image layer — whose preloading is purely an HTTP-cache concern,
   * because the browser decodes the PNG — a geojson frame has to be *parsed*
   * before `setData` can take it. So the prefetch keeps the parsed object, and
   * the memory cost is bounded by the prefetch window rather than by the frame
   * count.
   */
  private parsed = new Map<string, VectorCollection>()
  /** In-flight or finished fetches, so a scrub does not re-ask. */
  private warmed = new Map<string, Promise<VectorCollection | null>>()

  constructor(private readonly opts: GeoJsonFramesOptions) {
    this.id = opts.id
    this.opacity = opts.opacity
    this.attribution = opts.attribution
  }

  get times(): readonly Date[] {
    return this.frames.map((f) => f.time)
  }

  currentTime(): Date | null {
    return this.frames[this.current]?.time ?? null
  }

  load(): Promise<void> {
    this.loaded ??= this.opts
      .loadFrames()
      .then(async (found) => {
        if (!found || found.frames.length === 0) {
          this.frames = []
          return
        }
        this.frames = [...found.frames].sort((a, b) => a.time.getTime() - b.time.getTime())
        if (found.attribution) this.attribution = found.attribution
        this.current = 0
        // The first frame is awaited, so `add()` has something to draw rather
        // than mounting an empty source and filling it a tick later — which
        // looks exactly like a product with no area today.
        await this.fetchFrame(this.frames[0].url)
      })
      .catch((e) => {
        // An empty vector overlay is indistinguishable from a quiet day, so a
        // degraded path a user is meant to notice gets a console line.
        console.warn(`${this.id}: could not load model vectors`, e)
        this.frames = []
      })
    return this.loaded
  }

  private get key(): string {
    return `wx:${this.id}`
  }

  /** What the source should be holding right now. */
  private collection(): VectorCollection {
    const frame = this.frames[this.current]
    return (frame && this.parsed.get(frame.url)) ?? EMPTY
  }

  add(map: MapLibreMap, beforeId?: string): void {
    this.map = map
    if (this.frames.length === 0) return
    if (map.getSource(this.key)) return

    // `attribution` belongs in the spec here, unlike the image source: a
    // geojson source declares one in MapLibre's own style spec, which is
    // exactly the distinction `imageFramesLayer.ts` had to learn the hard way.
    map.addSource(this.key, {
      type: 'geojson',
      data: this.collection(),
      attribution: this.attribution,
    })

    this.styleLayers = this.opts.addLayers(map, this.key, this.opacity, beforeId)
    if (!this.visible) this.applyVisibility()
    this.opts.onFrame?.(map, this.collection())
    this.prefetch()
  }

  remove(map: MapLibreMap): void {
    for (const id of this.styleLayers) {
      if (map.getLayer(id)) map.removeLayer(id)
    }
    this.styleLayers = []
    if (map.getSource(this.key)) map.removeSource(this.key)
    if (this.map === map) this.map = null
  }

  setOpacity(opacity: number): void {
    this.opacity = opacity
    const map = this.map
    if (!map) return
    for (const id of this.styleLayers) {
      if (!map.getLayer(id)) continue
      const type = map.getLayer(id)?.type
      // One property per layer type rather than a guess: MapLibre throws on a
      // paint property a layer does not have.
      if (type === 'line') map.setPaintProperty(id, 'line-opacity', opacity)
      else if (type === 'symbol') map.setPaintProperty(id, 'icon-opacity', opacity)
      else if (type === 'fill') map.setPaintProperty(id, 'fill-opacity', opacity)
    }
  }

  setTime(time: Date): void {
    const next = nearestIndex(
      this.frames.map((f) => f.time),
      time,
    )
    if (next < 0 || next === this.current) return
    this.current = next
    void this.show(next)
    this.prefetch()
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return
    this.visible = visible
    this.applyVisibility()
    // A layer nobody can see should not be pulling frames. Opacity is not
    // visibility here either.
    if (visible) this.prefetch()
  }

  private applyVisibility(): void {
    const map = this.map
    if (!map) return
    for (const id of this.styleLayers) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', this.visible ? 'visible' : 'none')
      }
    }
  }

  ready(): Promise<void> {
    const map = this.map
    if (!map) return Promise.resolve()
    return new Promise((resolve) => map.once('idle', () => resolve()))
  }

  private async show(index: number): Promise<void> {
    const frame = this.frames[index]
    if (!frame) return
    if (!this.parsed.has(frame.url)) await this.fetchFrame(frame.url)
    // The cursor may have moved on while that was in flight; only the newest
    // selection draws.
    if (this.current !== index) return
    const source = this.map?.getSource(this.key) as GeoJSONSource | undefined
    source?.setData?.(this.collection())
    if (this.map) this.opts.onFrame?.(this.map, this.collection())
  }

  /**
   * Warm the frames around the cursor. Ahead by more than behind, because the
   * loop plays forward.
   *
   * The window also *bounds the memory*: anything outside it is dropped from
   * the parsed cache, so a 96-frame barb loop holds five documents rather than
   * ninety-six. The bytes are still in the browser's HTTP cache — the URLs are
   * `immutable` — so a scrub backwards re-parses without a network round trip.
   */
  private prefetch(): void {
    if (!this.visible || typeof fetch !== 'function') return
    const from = Math.max(0, this.current - (this.opts.prefetchBehind ?? DEFAULT_BEHIND))
    const to = Math.min(
      this.frames.length - 1,
      this.current + (this.opts.prefetchAhead ?? DEFAULT_AHEAD),
    )
    const keep = new Set<string>()
    for (let i = from; i <= to; i++) {
      keep.add(this.frames[i].url)
      void this.fetchFrame(this.frames[i].url)
    }
    for (const url of [...this.parsed.keys()]) {
      if (!keep.has(url)) {
        this.parsed.delete(url)
        this.warmed.delete(url)
      }
    }
  }

  /**
   * One frame, fetched and parsed at most once while it is in the window.
   *
   * Never throws: a frame that will not load leaves the layer drawing the one
   * it has, which is the same degradation every other overlay here makes.
   */
  private fetchFrame(url: string): Promise<VectorCollection | null> {
    const pending = this.warmed.get(url)
    if (pending) return pending
    const started = (async () => {
      try {
        const resp = await fetch(url)
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const body = (await resp.json()) as VectorCollection
        this.parsed.set(url, body)
        return body
      } catch (e) {
        console.warn(`${this.id}: ${url} failed; drawing nothing for that frame`, e)
        this.warmed.delete(url)
        return null
      }
    })()
    this.warmed.set(url, started)
    return started
  }
}
